import Phaser from 'phaser'
import { Player } from '../entities/Player'
import { LootBag } from '../entities/LootBag'
import {
  CreatureType,
  LOOT_TABLES,
  ITEMS,
  InventoryItem,
  EVENTS,
  LOOT_EVENTS
} from '../types/GameTypes'
import { InventorySystem } from './InventorySystem'

export class LootSystem {
  private scene: Phaser.Scene
  private player: Player
  private inventory: InventorySystem

  // Active loot bags in the world
  private lootBags: LootBag[] = []

  // Phaser group for loot bags
  private lootBagGroup: Phaser.GameObjects.Group

  constructor(
    scene: Phaser.Scene,
    player: Player,
    inventory: InventorySystem
  ) {
    this.scene = scene
    this.player = player
    this.inventory = inventory

    // Create loot bag group
    this.lootBagGroup = scene.add.group()

    // Listen for creature deaths
    this.scene.events.on(EVENTS.CREATURE_DIED, this.onCreatureDied, this)
  }

  /**
   * Handle creature death - roll loot and spawn bag
   */
  private onCreatureDied(data: { entity: CreatureType; x: number; y: number }): void {
    const lootData = this.rollLoot(data.entity)

    if (lootData.items.length > 0 || lootData.gold > 0) {
      this.spawnLootBag(data.x, data.y, lootData.items, lootData.gold)
    }
  }

  /**
   * Roll loot based on creature type
   */
  private rollLoot(creatureType: CreatureType): { items: InventoryItem[]; gold: number } {
    const lootTable = LOOT_TABLES[creatureType]
    const items: InventoryItem[] = []
    let gold = 0

    for (const drop of lootTable) {
      // Roll chance
      if (Math.random() <= drop.chance) {
        // Calculate quantity
        const qty = Phaser.Math.Between(drop.minQty, drop.maxQty)

        if (drop.itemId === 'gold') {
          gold += qty
        } else {
          items.push({ itemId: drop.itemId, quantity: qty })
        }
      }
    }

    return { items, gold }
  }

  /**
   * Spawn a loot bag at position
   */
  private spawnLootBag(x: number, y: number, items: InventoryItem[], gold: number): void {
    const lootBag = new LootBag(this.scene, x, y, items, gold)
    this.lootBags.push(lootBag)
    this.lootBagGroup.add(lootBag)

    // Set up despawn callback
    lootBag.onDespawn = () => {
      this.removeLootBag(lootBag)
    }

    // Emit loot dropped event
    this.scene.events.emit(LOOT_EVENTS.LOOT_DROPPED, { x, y, items, gold })
  }

  /**
   * Remove a loot bag from tracking
   */
  private removeLootBag(bag: LootBag): void {
    const index = this.lootBags.indexOf(bag)
    if (index !== -1) {
      this.lootBags.splice(index, 1)
    }
  }

  /**
   * Check for player collision with loot bags and collect
   */
  public update(): void {
    if (this.player.isDead) return

    // Check each loot bag for proximity
    for (let i = this.lootBags.length - 1; i >= 0; i--) {
      const bag = this.lootBags[i]

      if (!bag.active) {
        this.lootBags.splice(i, 1)
        continue
      }

      // Check distance to player
      const distance = Phaser.Math.Distance.Between(
        this.player.x,
        this.player.y,
        bag.x,
        bag.y
      )

      // Auto-collect when player walks over (within 24px)
      if (distance < 24) {
        this.collectLootBag(bag)
      }
    }
  }

  /**
   * Collect a loot bag
   */
  private collectLootBag(bag: LootBag): void {
    const contents = bag.getContents()

    // Add gold
    if (contents.gold > 0) {
      this.inventory.addGold(contents.gold)
      this.showFloatingText(bag.x, bag.y, `+${contents.gold} gold`, 0xfbbf24)
    }

    // Add items
    for (const item of contents.items) {
      const added = this.inventory.addItem(item.itemId, item.quantity)
      if (added > 0) {
        const itemDef = ITEMS[item.itemId]
        if (itemDef) {
          this.showFloatingText(
            bag.x,
            bag.y - 15,
            `+${added} ${itemDef.name}`,
            0x22c55e
          )
        }
      }
    }

    // Emit collected event
    this.scene.events.emit(LOOT_EVENTS.LOOT_COLLECTED, contents)

    // Remove bag
    this.removeLootBag(bag)
    bag.collect()
  }

  /**
   * Show floating text above position
   */
  private showFloatingText(x: number, y: number, text: string, color: number): void {
    const floatText = this.scene.add.text(x, y - 20, text, {
      fontSize: '14px',
      color: `#${color.toString(16).padStart(6, '0')}`,
      fontFamily: 'Arial',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5).setDepth(1000)

    // Animate floating up and fading
    this.scene.tweens.add({
      targets: floatText,
      y: y - 50,
      alpha: 0,
      duration: 1500,
      ease: 'Power2',
      onComplete: () => {
        floatText.destroy()
      }
    })
  }

  /**
   * Get all active loot bags
   */
  public getLootBags(): LootBag[] {
    return this.lootBags
  }

  /**
   * Get the loot bag group for collision detection
   */
  public getLootBagGroup(): Phaser.GameObjects.Group {
    return this.lootBagGroup
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off(EVENTS.CREATURE_DIED, this.onCreatureDied, this)

    // Destroy all loot bags
    for (const bag of this.lootBags) {
      bag.destroy()
    }
    this.lootBags = []
    this.lootBagGroup.destroy(true)
  }
}
